(() => {
  const SELECTORS = {
    finder: '[data-pl-gift-finder]',
    showcase: '[data-pl-showcase]',
    advent: '[data-pl-advent-teaser]'
  };

  class PLGiftFinder {
    constructor(root) {
      this.root = root;
      this.vibeButtons = [...root.querySelectorAll('[data-finder-vibe]')];
      this.budgetButtons = [...root.querySelectorAll('[data-finder-budget]')];
      this.results = [...root.querySelectorAll('[data-finder-result]')];

      this.vibe = this.vibeButtons.find(b => b.classList.contains('is-active'))?.dataset.finderVibe || '1';
      this.budget = this.budgetButtons.find(b => b.classList.contains('is-active'))?.dataset.finderBudget || '1';

      this.bind();
      this.render();
    }

    bind() {
      this.vibeButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.vibe = button.dataset.finderVibe;
          this.vibeButtons.forEach(b => b.classList.toggle('is-active', b === button));
          this.vibeButtons.forEach(b => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
          this.render();
        });
      });

      this.budgetButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.budget = button.dataset.finderBudget;
          this.budgetButtons.forEach(b => b.classList.toggle('is-active', b === button));
          this.budgetButtons.forEach(b => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
          this.render();
        });
      });
    }

    render() {
      let matched = false;

      this.results.forEach(result => {
        const active = result.dataset.vibe === this.vibe && result.dataset.budget === this.budget;
        result.classList.toggle('is-active', active);
        result.hidden = !active;
        if (active) matched = true;
      });

      if (!matched) {
        const fallback = this.results[0];
        if (fallback) {
          fallback.classList.add('is-active');
          fallback.hidden = false;
        }
      }
    }
  }

  class PLShowcase {
    constructor(root) {
      this.root = root;
      this.tabs = [...root.querySelectorAll('[data-showcase-tab]')];
      this.cards = [...root.querySelectorAll('[data-showcase-card]')];

      if (!this.tabs.length) return;

      this.active = this.tabs.find(tab => tab.classList.contains('is-active'))?.dataset.showcaseTab || '1';
      this.bind();
      this.render();
    }

    bind() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.active = tab.dataset.showcaseTab;
          this.render();
        });
      });
    }

    render() {
      this.tabs.forEach(tab => {
        const active = tab.dataset.showcaseTab === this.active;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.cards.forEach(card => {
        const active = card.dataset.group === this.active;
        card.hidden = !active;

        if (!active) {
          card.querySelectorAll('video').forEach(video => {
            try { video.pause(); } catch (_) {}
          });
        }
      });
    }
  }


  class PLAdventTeaser {
    constructor(root) {
      this.root = root;
      this.doors = [...root.querySelectorAll('[data-advent-door]')];
      this.templates = [...root.querySelectorAll('[data-advent-template]')];
      this.modal = root.querySelector('[data-advent-modal]');
      this.dialog = root.querySelector('[data-advent-dialog]');
      this.body = root.querySelector('[data-advent-modal-body]');
      this.prev = root.querySelector('[data-advent-prev]');
      this.next = root.querySelector('[data-advent-next]');
      this.closeButtons = [...root.querySelectorAll('[data-advent-close]')];

      this.activeIndex = 0;
      this.lastFocus = null;

      if (!this.doors.length) return;

      this.bind();
    }

    bind() {
      this.doors.forEach((door, index) => {
        door.addEventListener('click', () => this.open(index, door));
      });

      this.prev?.addEventListener('click', () => this.change(-1));
      this.next?.addEventListener('click', () => this.change(1));
      this.closeButtons.forEach(button => button.addEventListener('click', () => this.close()));

      document.addEventListener('keydown', event => {
        if (!this.modal || this.modal.hidden || !this.root.isConnected) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.change(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.change(1);
        }
      });
    }

    wrap(index) {
      return (index + this.doors.length) % this.doors.length;
    }

    open(index, trigger) {
      if (!this.modal || !this.dialog || !this.body) return;

      this.activeIndex = this.wrap(index);
      this.lastFocus = trigger || document.activeElement;

      this.doors[this.activeIndex]?.classList.add('is-viewed');
      this.populate();

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pl-advent-modal-open');

      requestAnimationFrame(() => this.dialog.focus());
    }

    populate() {
      const template = this.templates[this.activeIndex];
      if (!template || !this.body) return;

      this.body.innerHTML = template.innerHTML;
      this.dialog?.scrollTo({ top: 0, behavior: 'auto' });
      this.doors[this.activeIndex]?.classList.add('is-viewed');
    }

    change(direction) {
      this.activeIndex = this.wrap(this.activeIndex + direction);
      this.populate();
    }

    close() {
      if (!this.modal || this.modal.hidden) return;

      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pl-advent-modal-open');

      this.lastFocus?.focus?.();
      this.lastFocus = null;
    }
  }

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.finder).forEach(root => {
      if (root.dataset.plInitialised) return;
      root.dataset.plInitialised = '1';
      new PLGiftFinder(root);
    });

    scope.querySelectorAll(SELECTORS.showcase).forEach(root => {
      if (root.dataset.plInitialised) return;
      root.dataset.plInitialised = '1';
      new PLShowcase(root);
    });

    scope.querySelectorAll(SELECTORS.advent).forEach(root => {
      if (root.dataset.plInitialised) return;
      root.dataset.plInitialised = '1';
      new PLAdventTeaser(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
})();
